import { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { LayoutDashboard, TrendingUp, BarChart3, LineChart, Settings, Zap, Sparkles, User, LogOut, Star, Filter, PieChart, Bell, BellRing, Sun, Moon } from 'lucide-react';
import Dashboard from './components/Dashboard';
import TradeList from './components/TradeList';
import ProfitChart from './components/ProfitChart';




import { LoginPage } from './pages/LoginPage';
import { LandingPage, type Lang } from './pages/LandingPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { ChangelogPage } from './pages/ChangelogPage';
import { NotFoundPage } from './pages/NotFoundPage';

import { AuthProvider, useAuth } from './context/AuthContext';
import { useAnalytics } from './hooks/useAnalytics';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { NotificationProvider, useNotifications } from './context/NotificationContext';
import { SkeletonCard } from './components/skeletons';
import { MobileNav } from './components/MobileNav';

const FlowPage = lazy(() => import('./pages/FlowPage').then(m => ({ default: m.FlowPage })));
const TTrader = lazy(() => import('./pages/TTrader').then(m => ({ default: m.TTrader })));
const AIAnalysisPage = lazy(() => import('./pages/AIAnalysisPage').then(m => ({ default: m.AIAnalysisPage })));
const StatsPage = lazy(() => import('./components/StatsPage').then(m => ({ default: m.StatsPage })));
const WatchlistPage = lazy(() => import('./pages/WatchlistPage').then(m => ({ default: m.WatchlistPage })));
const ScreenerPage = lazy(() => import('./pages/ScreenerPage').then(m => ({ default: m.ScreenerPage })));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage').then(m => ({ default: m.PortfolioPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));
const AlertsPage = lazy(() => import('./pages/AlertsPage').then(m => ({ default: m.AlertsPage })));
const BacktestPage = lazy(() => import('./pages/BacktestPage').then(m => ({ default: m.BacktestPage })));

type Page = 'main' | 'flow' | 'watchlist' | 'screener' | 'portfolio' | 't-trader' | 'ai' | 'stats' | 'backtest' | 'alerts' | 'settings' | 'admin';

const navItems: { key: Page; label: string; icon: React.ReactNode }[] = [
  { key: 'main', label: '总览', icon: <LayoutDashboard className="h-4 w-4" /> },
  { key: 'flow', label: '资金流向', icon: <Zap className="h-4 w-4" /> },
  { key: 'watchlist', label: '自选股', icon: <Star className="h-4 w-4" /> },
  { key: 'screener', label: '选股器', icon: <Filter className="h-4 w-4" /> },
  { key: 'portfolio', label: '组合', icon: <PieChart className="h-4 w-4" /> },
  { key: 't-trader', label: '做T助手', icon: <TrendingUp className="h-4 w-4" /> },
  { key: 'ai', label: 'AI分析', icon: <Sparkles className="h-4 w-4" /> },
  { key: 'stats', label: '统计·账户', icon: <BarChart3 className="h-4 w-4" /> },
  { key: 'backtest', label: '策略回测', icon: <LineChart className="h-4 w-4" /> },
  { key: 'alerts', label: '价格预警', icon: <BellRing className="h-4 w-4" /> },
  { key: 'settings', label: '设置', icon: <Settings className="h-4 w-4" /> },
  { key: 'admin', label: '后台管理', icon: <User className="h-4 w-4" /> },
];

function getInitialPublicPage(): 'landing' | 'login' | 'terms' | 'privacy' | 'changelog' | 'notfound' {
  const path = window.location.pathname;
  const hash = window.location.hash.replace('#', '');
  if (path === '/terms' || hash === 'terms') return 'terms';
  if (path === '/privacy' || hash === 'privacy') return 'privacy';
  if (path === '/changelog' || hash === 'changelog') return 'changelog';
  if (path === '/login' || hash === 'login') return 'login';
  if (path !== '/' && !hash) return 'notfound';
  return 'landing';
}

function AppContent() {
  useAnalytics();
  const [activePage, setActivePage] = useState<Page>('flow');
  const { user, loading, logout } = useAuth();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [authMode, setAuthMode] = useState<'landing' | 'login'>('landing');
  const [loginMode, setLoginMode] = useState<'email' | 'wechat'>('email');
  const [publicPage, setPublicPage] = useState(getInitialPublicPage());
  const [lang, setLang] = useState<Lang>('zh');
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleHashChange() {
      setPublicPage(getInitialPublicPage());
    }
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  const {
    notifications,
    unreadCount,
    markNotificationRead,
    markAllRead,
    deleteNotification,
  } = useNotifications();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#533afd] border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    const showLogin = authMode === 'login' || publicPage === 'login';
    if (showLogin) {
      return (
        <LoginPage
          onSuccess={() => window.location.reload()}
          onBack={() => {
            setAuthMode('landing');
            setPublicPage('landing');
            setLoginMode('email');
          }}
          initialMode={loginMode}
        />
      );
    }
    if (publicPage === 'terms') {
      return <TermsPage lang={lang} setLang={setLang} onBack={() => setPublicPage('landing')} />;
    }
    if (publicPage === 'privacy') {
      return <PrivacyPage lang={lang} setLang={setLang} onBack={() => setPublicPage('landing')} />;
    }
    if (publicPage === 'changelog') {
      return <ChangelogPage lang={lang} setLang={setLang} onBack={() => setPublicPage('landing')} />;
    }
    if (publicPage === 'notfound') {
      return <NotFoundPage onBack={() => setPublicPage('landing')} />;
    }
    return (
      <LandingPage
        onLogin={() => setAuthMode('login')}
        lang={lang}
        setLang={setLang}
        onPublicPage={(page) => setPublicPage(page)}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-[12px]">
        <div className="flex h-16 items-center px-4 md:px-6 lg:px-10 gap-4 md:gap-6 w-full">
          <MobileNav items={navItems} activePage={activePage} onNavigate={setActivePage} />
          <a href="#" className="flex items-center gap-2 text-[16px] font-[400] text-foreground tracking-[-0.02em] no-underline">
            <img src="/LOGO.png" alt="TingYue" className="h-7 w-auto" />
            <span className="text-lg">TingYue</span>
          </a>

          <nav className="hidden md:flex items-center gap-1 overflow-x-auto">
            {navItems.map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => setActivePage(key)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-[8px] text-[15px] font-medium transition-colors whitespace-nowrap ${
                  activePage === key
                    ? 'bg-[#533afd] text-white'
                    : 'text-foreground hover:bg-[rgba(83,58,253,0.05)] hover:text-[#533afd]'
                }`}
              >
                {icon}
                {label}
              </button>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden md:inline max-w-[120px] truncate" title={user.email}>
              {user.phone ? user.phone : (user.nickname || user.email)}
            </span>
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative text-muted-foreground hover:text-[#533afd] transition p-1"
                title="通知"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] max-w-sm bg-popover text-popover-foreground rounded-xl border border-border shadow-lg z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <span className="text-sm font-medium">通知</span>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-xs text-[#533afd] hover:underline">
                        全部已读
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-xs text-muted-foreground">暂无通知</div>
                    ) : (
                      notifications.map(n => (
                        <div
                          key={n.id}
                          className={`px-4 py-3 border-b border-border hover:bg-secondary flex gap-3 ${n.read ? 'opacity-70' : 'bg-accent'}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{n.title}</div>
                            {n.message && <div className="text-xs text-muted-foreground truncate">{n.message}</div>}
                            <div className="text-[10px] text-muted-foreground mt-1">
                              {new Date(n.created_at).toLocaleString('zh-CN')}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {!n.read && (
                              <button
                                onClick={() => markNotificationRead(n.id)}
                                className="text-[10px] text-[#533afd] hover:underline"
                              >
                                已读
                              </button>
                            )}
                            <button
                              onClick={() => deleteNotification(n.id)}
                              className="text-[10px] text-muted-foreground hover:text-red-500"
                            >
                              删除
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark')}
              className="text-muted-foreground hover:text-[#533afd] transition"
              title="切换主题"
            >
              {resolvedTheme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <button
              onClick={logout}
              className="text-muted-foreground hover:text-[#533afd] transition"
              title="退出登录"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full p-4 md:p-6 lg:px-10 lg:py-8">
        {activePage !== 't-trader' && (
          <div className="text-xs text-muted-foreground bg-secondary border border-border rounded-[6px] px-3 py-2 mb-4">
            {activePage === 'main' && '数据每10秒自动刷新，来自已记录的持仓和交易'}
            {activePage === 'flow' && '资金流向来自同花顺北向资金与板块数据，盘中实时推送'}
            {activePage === 'watchlist' && '自选股行情每30秒自动刷新，点击分析可跳转到做T助手'}
            {activePage === 'screener' && '基于你的自选股和持仓股票进行条件筛选，行情实时获取'}
            {activePage === 'portfolio' && '创建多股票组合，实时跟踪总市值、收益率和各持仓贡献'}
            {activePage === 'ai' && '基于价格行为框架的两阶段 AI 分析，支持多模型综合结论'}
            {activePage === 'stats' && '选择时间范围查看交易统计和配对盈亏明细'}
            {activePage === 'backtest' && '基于历史行情验证策略收益，生成可视化回测报告'}
            {activePage === 'alerts' && '设置目标价，股价突破或跌破时自动通知'}
            {activePage === 'settings' && '配置大模型 API Key 与账户相关设置'}
            {activePage === 'admin' && '仅管理员可见：用户查找、角色管理、重置密码与平台统计'}
          </div>
        )}

        <Suspense
          fallback={
            <div className="space-y-6 py-4">
              <SkeletonCard />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <SkeletonCard />
                  <SkeletonCard />
                </div>
                <SkeletonCard />
              </div>
            </div>
          }
        >
          {activePage === 'main' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Dashboard />
                <ProfitChart />
              </div>
              <div>
                <TradeList />
              </div>
            </div>
          ) : activePage === 'flow' ? (
            <FlowPage />
          ) : activePage === 'watchlist' ? (
            <WatchlistPage />
          ) : activePage === 'screener' ? (
            <ScreenerPage />
          ) : activePage === 'portfolio' ? (
            <PortfolioPage />
          ) : activePage === 'ai' ? (
            <AIAnalysisPage />
          ) : activePage === 'stats' ? (
            <StatsPage />
          ) : activePage === 'backtest' ? (
            <BacktestPage />
          ) : activePage === 'alerts' ? (
            <AlertsPage />
          ) : activePage === 'settings' ? (
            <SettingsPage />
          ) : activePage === 'admin' ? (
            <AdminPage />
          ) : (
            <TTrader />
          )}
        </Suspense>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
